import { stimulusFileUrl } from '../../utils/stimulusFileUrl';
import { sanitizeHtml } from '../../components-mobile/sanitizeHtml';

export const stripHtml = (html) => {
  if (!html) {
    return '';
  }
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// Stimulus text is stored verbatim and shown to other users (students, the
// recipients of a shared task), so it is never rendered as raw HTML. This keeps
// the formatting stored stimulus actually carries (the editor's own formats,
// pasted text colours and highlights, tables) and drops everything else.
//
// Views call this on every render and stored stimulus runs to megabytes, so
// recent results are cached (the output depends only on the input string).
const SANITIZED_CACHE_SIZE = 10;
const sanitizedStimulusCache = new Map();
export const sanitizeStimulusHtml = (html) => {
  const key = String(html || '');
  if (sanitizedStimulusCache.has(key)) return sanitizedStimulusCache.get(key);
  const clean = sanitizeHtml(key, {
    preserveQuillSizes: true,
    preserveColors: true,
    preserveTables: true,
  });
  if (sanitizedStimulusCache.size >= SANITIZED_CACHE_SIZE) {
    sanitizedStimulusCache.delete(sanitizedStimulusCache.keys().next().value);
  }
  sanitizedStimulusCache.set(key, clean);
  return clean;
};

export const isRichTextEmpty = (html) => {
  return stripHtml(html).length === 0;
};

export const normalizeStimulusText = (html) => {
  return isRichTextEmpty(html) ? '' : html;
};

// Generated stimulus is plain text; manually authored stimulus may be rich
// text. Preserve line breaks without interpreting plain text as HTML.
export const stimulusTextPreview = (text, name = 'Text 1') => ({
  fileType: 'TEXT',
  name,
  ...(/<\/?(?:p|div|br|h[1-6]|strong|em|ul|ol|li|span|blockquote)\b/i.test(text || '')
    ? { html: text }
    : { text: text || '' }),
});

export const hasStimulus = (question) => {
  if (!question) {
    return false;
  }
  const hasText = !isRichTextEmpty(question.stimulusText);
  const hasFiles =
    Array.isArray(question.stimulusFiles) && question.stimulusFiles.length > 0;
  return hasText || hasFiles;
};

export const buildStimulusItems = (question) => {
  if (!question) {
    return [];
  }
  const items = [];
  let textIndex = 0;
  let imageIndex = 0;

  if (question.stimulusText && !isRichTextEmpty(question.stimulusText)) {
    textIndex += 1;
    items.push({
      id: 'stimulus-text',
      kind: 'text',
      title: `Text ${textIndex}`,
      html: question.stimulusText,
    });
  }

  const files = Array.isArray(question.stimulusFiles)
    ? question.stimulusFiles
    : [];
  files.forEach((file, index) => {
    if (!file) {
      return;
    }
    if (file.fileType === 'PDF') {
      textIndex += 1;
      items.push({
        id: file.id || `pdf-${index}`,
        kind: 'pdf',
        title: file.name || `Text ${textIndex}`,
        text: file.extractedText || '',
        imageUrls: (file.extractedImageUrls || []).map(stimulusFileUrl),
        url: stimulusFileUrl(file.url),
      });
      return;
    }
    imageIndex += 1;
    items.push({
      id: file.id || `image-${index}`,
      kind: 'image',
      title: file.name || `Image ${imageIndex}`,
      url: stimulusFileUrl(file.url),
    });
  });

  return items;
};
